import { z } from 'zod'

import { FormFieldType, FormPublicLinkStatus } from '../enums'

import { allowedFileTypeSchema } from './file'

// Who may see and change one field. `access` is only sent to owners and admins; everyone gets
// `myAccess`, the level the signed-in user has (a missing value means the server predates field access).
export const fieldAccessModeSchema = z.enum(['OPEN', 'EDIT_RESTRICTED', 'RESTRICTED'])
export const fieldAccessLevelSchema = z.enum(['EDIT', 'VIEW', 'NONE'])

export const fieldAccessSchema = z.object({
  mode: fieldAccessModeSchema,
  viewRoleIds: z.array(z.string().uuid()).optional(),
  viewUserIds: z.array(z.string().uuid()).optional(),
  editRoleIds: z.array(z.string().uuid()).optional(),
  editUserIds: z.array(z.string().uuid()).optional(),
})

export const formFieldSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  type: z.nativeEnum(FormFieldType),
  required: z.boolean(),
  options: z.record(z.unknown()).nullable(),
  validations: z.record(z.unknown()).nullable(),
  orderIndex: z.number().int().nonnegative().optional(),
  access: fieldAccessSchema.nullable().optional(),
  myAccess: fieldAccessLevelSchema.optional(),
})

export const formBasicInfoSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  recordGroupId: z.string().uuid(),
  mainFieldId: z.string().uuid().nullable().optional(),
  mainFieldKey: z.string().nullable().optional(),
})

export const formSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  recordGroupId: z.string().uuid(),
  mainFieldId: z.string().uuid().nullable().optional(),
  mainFieldKey: z.string().nullable().optional(),
  fields: z.array(formFieldSchema),
})

export const formTemplateFieldSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  type: z.nativeEnum(FormFieldType),
  required: z.boolean(),
  options: z.record(z.unknown()).nullable(),
  validations: z.record(z.unknown()).nullable(),
  orderIndex: z.number().int().nonnegative().optional(),
})

export const formTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  mainFieldId: z.string().uuid().nullable().optional(),
  mainFieldKey: z.string().nullable().optional(),
  /** PLATFORM = shipped for every workspace, WORKSPACE = saved by this workspace. */
  scope: z.enum(['PLATFORM', 'WORKSPACE']),
  fields: z.array(formTemplateFieldSchema),
})

export const createFormFieldRequestSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.nativeEnum(FormFieldType),
  required: z.boolean(),
  options: z.record(z.unknown()).nullable().optional(),
  validations: z.record(z.unknown()).nullable().optional(),
  orderIndex: z.number().int().nonnegative(),
  // Only owners and admins send this; for everyone else the server keeps the previous version's.
  access: fieldAccessSchema.nullable().optional(),
})

export const createFormRequestSchema = z.object({
  recordGroupId: z.string().uuid(),
  fields: z.array(createFormFieldRequestSchema),
  mainFieldId: z.string().uuid().nullable().optional(),
  mainFieldKey: z.string().nullable().optional(),
})

export const updateFormRequestSchema = createFormRequestSchema.extend({
  id: z.string().uuid(),
})

export const createFormResponseSchema = z.object({
  id: z.string().uuid(),
})

export const createFormTemplateRequestSchema = z.object({
  formName: z.string().min(1).max(120),
  formDescription: z.string().max(255).optional(),
  fields: z.array(createFormFieldRequestSchema).min(1),
  mainFieldKey: z.string().nullable().optional(),
  /** Developers only: share with every workspace. */
  platform: z.boolean().optional(),
})

export type FieldAccess = z.infer<typeof fieldAccessSchema>
export type FieldAccessLevel = z.infer<typeof fieldAccessLevelSchema>
export type FormField = z.infer<typeof formFieldSchema>
export type FormBasicInfo = z.infer<typeof formBasicInfoSchema>
export type Form = z.infer<typeof formSchema>
export type FormTemplateField = z.infer<typeof formTemplateFieldSchema>
export type FormTemplate = z.infer<typeof formTemplateSchema>
export type CreateFormFieldRequest = z.infer<typeof createFormFieldRequestSchema>
export type CreateFormRequest = z.infer<typeof createFormRequestSchema>
export type UpdateFormRequest = z.infer<typeof updateFormRequestSchema>
export type CreateFormResponse = z.infer<typeof createFormResponseSchema>
export type CreateFormTemplateRequest = z.infer<typeof createFormTemplateRequestSchema>

// ── Public link types ────────────────────────────────────────────────────────

export const formPublicLinkSchema = z.object({
  id: z.string().uuid(),
  token: z.string(),
  formId: z.string().uuid(),
  formVersion: z.number().int(),
  recordGroupId: z.string().uuid(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  successMessage: z.string().nullable().optional(),
  redirectUrl: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  maxSubmissions: z.number().int().nullable().optional(),
  submissionCount: z.number().int(),
  notifyOnSubmissionCount: z.number().int().nullable().optional(),
  active: z.boolean(),
  status: z.nativeEnum(FormPublicLinkStatus),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})

export const createPublicLinkRequestSchema = z.object({
  formId: z.string().uuid(),
  title: z.string().optional(),
  description: z.string().optional(),
  successMessage: z.string().optional(),
  redirectUrl: z.string().optional(),
  expiresAt: z.string().nullable().optional(),
  maxSubmissions: z.number().int().positive().nullable().optional(),
  notifyOnSubmissionCount: z.number().int().positive().nullable().optional(),
})

export const publicFormSchemaResponseSchema = z.object({
  workspaceName: z.string(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  successMessage: z.string().nullable().optional(),
  redirectUrl: z.string().nullable().optional(),
  fields: z.array(formFieldSchema),
  // File-upload limits so the public page can validate before uploading.
  maxFileSizeBytes: z.number().int().positive().optional(),
  allowedFileTypes: z.array(allowedFileTypeSchema).optional(),
})

export type FormPublicLink = z.infer<typeof formPublicLinkSchema>
export type CreatePublicLinkRequest = z.infer<typeof createPublicLinkRequestSchema>
export type PublicFormSchema = z.infer<typeof publicFormSchemaResponseSchema>

